<template>
  <a-breadcrumb style="margin: 16px 0">
    <a-breadcrumb-item>贡献者</a-breadcrumb-item>
    <a-breadcrumb-item>活跃度分析</a-breadcrumb-item>
  </a-breadcrumb>

  <contributor-overview-days />

  <div class="container">
    <div class="title-container">
      <span class="title">活跃度分析</span>
    </div>
    <div style="text-align: left;">
      <span class="caption-and-select-container">
        <span class="caption">项目维度</span>
        <a-select v-model:value="project" class="select">
          <a-select-option v-for="opt in projectOptions" :key="opt.key" :value="opt.value">
            {{ opt.label }}
          </a-select-option>
        </a-select>
      </span>
      <span class="caption-and-select-container">
        <span class="caption">组织维度</span>
        <my-dropdown-checkbox v-model:checkedList="employerCheckedList" :options="employerOptions"
          :label-formatter="(checked, total) => checked === total ? '全部' : `已筛选 ${checked}/${total}`" />
      </span>
    </div>
    <div class=" chart-table-title" style="margin-top: 32px;">
      ◉ 年维度分析
    </div>
    <a-spin :spinning="yearSpinning">
      <div style="padding: 16px;">
        <MyLineChart :data="lineChartData" :colors="lineColors" />
      </div>
      <div style="padding: 0 16px; margin-top: 12px;">
        <a-table :columns="yearTableColumns" :data-source="yearTableData" :pagination="false"
          :rowKey="row => row.type" />
      </div>
    </a-spin>
    <div style="display: flex; justify-content: space-between; align-items: center; margin-top: 32px;">
      <div class="chart-table-title">
        ◉ 月维度分析
      </div>
      <span class="caption-and-select-container">
        <span class="caption">年份</span>
        <a-select v-model:value="year" class="select">
          <a-select-option v-for="opt in yearOptions" :key="opt.key" :value="opt.value">
            {{ opt.label }}
          </a-select-option>
        </a-select>
      </span>
    </div>
    <a-spin :spinning="monthSpinning">
      <div style="padding: 16px;">
        <MyLineChart :data="monthChartData" :colors="monthColors" />
      </div>
      <div style="padding: 0 16px; margin-top: 12px;">
        <a-table :columns="monthTableColumns" :data-source="monthTableData" :pagination="false"
          :rowKey="row => row.type" />
      </div>
    </a-spin>

  </div>
</template>

<script setup>
// Imports
import { ref, watch, onMounted } from 'vue';
import MyLineChart from '@/measurement-platform/components/MyLineChart.vue';
import ContributorOverviewDays from './ContributorOverviewDays.vue';
import MyDropdownCheckbox from '@/measurement-platform/components/MyDropdownCheckbox.vue';
import axios from 'axios';
import { notification } from 'ant-design-vue';

// Select options
const projectOptions = [
  { key: 'all', label: '全部', value: null },
  { key: 'master', label: '主干', value: 'openharmony' },
  { key: 'sig', label: '孵化仓', value: 'openharmony-sig' },
  { key: 'tpc', label: 'TPC', value: 'openharmony-tpc' }
];
const employerOptions = ['华为', '伙伴：哈勃', '伙伴：捐赠单位', '伙伴：其他', '高校', '其他'];
const employerValueMap = {
  '华为': 'HUAWEI',
  '伙伴：哈勃': 'HABO',
  '伙伴：捐赠单位': 'DONATE',
  '伙伴：其他': 'PARTNER',
  '高校': 'SCHOOL',
  '其他': 'OTHERS'
};
const years = Array.from({ length: 2025 - 2020 + 1 }, (_, i) => 2020 + i);
const yearOptions = years.map((y) => ({ key: y, label: String(y), value: y }));
const months = ['1月', '2月', '3月', '4月', '5月', '6月', '7月', '8月', '9月', '10月', '11月', '12月'];

// Colors
const lineColors = ['#10B981', '#3B82F6', '#EF4444'];
const monthColors = ['#10B981', '#3B82F6'];

// Reactive state
const project = ref(null);
const employerCheckedList = ref([...employerOptions]);
const year = ref(2025);

const lineChartData = ref({ years: [], series: [] });
const yearTableColumns = ref([]);
const yearTableData = ref([]);
const yearSpinning = ref(false);

const monthChartData = ref({ years: months, series: [] });
const monthTableColumns = ref([
  { title: '类型', dataIndex: 'type', key: 'type', width: 100 },
  ...months.map(m => ({ title: m, dataIndex: m, key: m, align: 'right', width: 80 }))
]);
const monthTableData = ref([]);
const monthSpinning = ref(false);

const buildColumns = (labels) => ([
  // 表头：左侧固定“类型”，右侧是每个年份/月份
  { title: '类型', dataIndex: 'type', key: 'type', width: 100 },
  ...labels.map((label) => ({ title: label, dataIndex: label, key: label, align: 'right', width: 80 }))
]);

const buildRows = (labels, rows) => rows.map(({ type, values }) => (
  // 把数组 values 按 labels 填进表格每一列
  Object.assign({ type }, Object.fromEntries(labels.map((label, i) => [label, values[i]])))
));

// 年维度数据缓存（仅页面加载时请求一次）
// 后端返回格式示例（三个接口返回的 data 结构一致，仅语义不同）：
// {
//   status: 'S',
//   code: 200,
//   msg: 'Success',
//   desc: '操作成功',
//   data: [
//     {
//       commit_year: 2025,
//       count: 1498,
//       project: 'openharmony',
//       remarks: 'HUAWEI'
//     },
//     ...
//   ]
// }
// 本地缓存会整理为：{ 新增: data[], 活跃: data[], 流失: data[] }
const yearApiCache = ref(null);

// 仅展示 <= 2025 年的数据
const MAX_YEAR = 2025;

// 把“列表数据”按年份聚合成 Map：year => 选中组织的 count 之和
// 处理流程：
// 1) 过滤 project
// 2) 过滤 remarks（组织维度）
// 3) 过滤 MAX_YEAR
const buildYearMap = (items, proj, employerSet) => {
  const yearMap = new Map();
  if (!items) return yearMap;
  const targetProject = proj ?? 'all';
  const list = items.filter((it) => it.project === targetProject);
  const filtered = employerSet ? list.filter((it) => employerSet.has(it.remarks)) : list;
  filtered.forEach((it) => {
    const y = String(it.commit_year);
    if (Number(y) > MAX_YEAR) return;
    yearMap.set(y, (yearMap.get(y) || 0) + (Number(it.count) || 0));
  });
  return yearMap;
};

// 按 years 顺序输出每年的值，没有值就补 0
const buildYearValues = (yearMap, years) => years.map((y) => yearMap.get(y) || 0);

// 把前端的组织名称映射成后端枚举，形成过滤集合
// - 全选：返回 null（表示不过滤）
// - 全不选：返回空集合（后续显示 0 值占位）
// - 部分选择：返回对应枚举集合
const getEmployerFilterSet = () => {
  const selected = employerCheckedList.value;
  if (selected.length === employerOptions.length) return null;
  const mapped = selected.map((label) => employerValueMap[label]).filter(Boolean);
  return new Set(mapped);
};

// 收集某项目下所有出现过的年份（用于“全不选”时保留年份轴）
const collectYearsByProject = (items, proj) => {
  if (!items) return new Set();
  const targetProject = proj ?? 'all';
  return new Set(
    items
      .filter((it) => it.project === targetProject)
      .map((it) => String(it.commit_year))
      .filter((y) => Number(y) <= MAX_YEAR)
  );
};

// 根据缓存 + 当前筛选条件，生成折线图与表格
// 说明：
// - 正常情况：年份来自“筛选后的数据”
// - 全不选组织：年份来自“该项目所有年份”，数值全部置 0
const rebuildYearSeries = () => {
  if (!yearApiCache.value) return;
  const proj = project.value || null;
  const employerSet = getEmployerFilterSet();
  const isEmptySelection = employerCheckedList.value.length === 0;

  const addMap = buildYearMap(yearApiCache.value['新增'], proj, employerSet);
  const activeMap = buildYearMap(yearApiCache.value['活跃'], proj, employerSet);
  const lostMap = buildYearMap(yearApiCache.value['流失'], proj, employerSet);

  // 年份范围：正常情况按筛选后的数据年份；全不选时仍保留项目下所有年份，展示 0 占位
  const yearsSet = isEmptySelection
    ? new Set([
      ...collectYearsByProject(yearApiCache.value['新增'], proj),
      ...collectYearsByProject(yearApiCache.value['活跃'], proj),
      ...collectYearsByProject(yearApiCache.value['流失'], proj)
    ])
    : new Set([...addMap.keys(), ...activeMap.keys(), ...lostMap.keys()]);
  const yearsArr = Array.from(yearsSet).sort();

  // 将三条曲线的 values 对齐到同一年份顺序
  const buildValues = (map) => (isEmptySelection ? yearsArr.map(() => 0) : buildYearValues(map, yearsArr));
  const addVals = buildValues(addMap);
  const activeVals = buildValues(activeMap);
  const lostVals = buildValues(lostMap);
  lineChartData.value = {
    years: yearsArr,
    series: [
      { name: '新增', values: addVals },
      { name: '活跃', values: activeVals },
      { name: '流失', values: lostVals }
    ]
  };
  yearTableColumns.value = buildColumns(yearsArr);
  yearTableData.value = buildRows(yearsArr, [
    { type: '新增', values: addVals },
    { type: '活跃', values: activeVals },
    { type: '流失', values: lostVals }
  ]);
};

// 页面加载时请求一次年维度数据并缓存
// 三个接口：新增 / 活跃 / 流失
const fetchYearSeries = async () => {
  yearSpinning.value = true;
  try {
    const requests = [
      { label: '新增', url: '/contributorController/getAllNewContributorByYear' },
      { label: '活跃', url: '/contributorController/getActiveContributorByYear' },
      { label: '流失', url: '/contributorController/getLostContributorByYear' }
    ];
    const results = await Promise.allSettled(
      requests.map((r) => axios.get(r.url))
    );

    const dataByLabel = {};
    results.forEach((result, index) => {
      const { label } = requests[index];
      if (result.status === 'rejected') {
        throw new Error(`${label}接口失败：${result.reason}`);
      }
      dataByLabel[label] = result.value?.data?.data || [];
    });
    yearApiCache.value = dataByLabel;
    rebuildYearSeries();
  } catch (e) {
    lineChartData.value = { years: [], series: [] };
    yearTableColumns.value = [];
    yearTableData.value = [];
    notification.error({ message: '年维度数据加载失败', description: e?.message || e });
  } finally {
    yearSpinning.value = false;
  }
};

// 页面进入时加载年维度数据
onMounted(() => {
  fetchYearSeries();
});

// 筛选变化时，基于缓存重新整理展示数据
// employerCheckedList 每次由组件 emit 新数组，watch 可直接生效
watch([project, employerCheckedList], () => rebuildYearSeries());

// 月维度缓存（按 project + year 存一份，组织维度只做前端重算）
// 缓存结构：
// {
//   [cacheKey]: {
//     新增: data[],
//     活跃: data[]
//   }
// }
// 接口返回格式示例：
// {
//   data: [
//     { remarks: 'HUAWEI', month1: 158, ..., month12: 135 },
//     { remarks: 'SCHOOL', month1: 1, ..., month12: 10 },
//     ...
//   ]
// }
const monthApiCache = ref({});

const buildMonthCacheKey = () => {
  const projKey = project.value || 'all';
  const yearKey = year.value ?? 'all';
  return `${projKey}__${yearKey}`;
};

const toMonthValues = (item) => months.map((_m, idx) => Number(item?.[`month${idx + 1}`]) || 0);

const rebuildMonthSeries = () => {
  const cacheKey = buildMonthCacheKey();
  const cached = monthApiCache.value[cacheKey] || { 新增: [], 活跃: [] };
  const employerSet = getEmployerFilterSet();
  const isEmptySelection = employerCheckedList.value.length === 0;

  const filterByEmployer = (list) => (
    isEmptySelection
      ? []
      : (employerSet ? list.filter((it) => employerSet.has(it.remarks)) : list)
  );

  const sumMonthly = (list) => {
    const sumByMonth = Array.from({ length: 12 }, () => 0);
    list.forEach((item) => {
      const vals = toMonthValues(item);
      vals.forEach((v, i) => {
        sumByMonth[i] += v;
      });
    });
    return sumByMonth;
  };

  const addVals = isEmptySelection ? Array.from({ length: 12 }, () => 0) : sumMonthly(filterByEmployer(cached.新增));
  const activeVals = isEmptySelection ? Array.from({ length: 12 }, () => 0) : sumMonthly(filterByEmployer(cached.活跃));

  monthChartData.value = {
    years: months,
    series: [
      { name: '新增', values: addVals },
      { name: '活跃', values: activeVals }
    ]
  };
  monthTableData.value = buildRows(months, [
    { type: '新增', values: addVals },
    { type: '活跃', values: activeVals }
  ]);
};

const fetchMonthSeries = async () => {
  monthSpinning.value = true;
  try {
    const cacheKey = buildMonthCacheKey();
    if (!monthApiCache.value[cacheKey]) {
      const params = { year: year.value, project: project.value || null };
      const [newRes, activeRes] = await Promise.all([
        axios.get('/contributorController/getNewContributorByMonth', { params }),
        axios.get('/contributorController/getActiveContributorByMonth', { params })
      ]);
      if (newRes.data?.code !== 200) {
        throw new Error(newRes.data?.msg || '请求失败');
      }
      if (activeRes.data?.code !== 200) {
        throw new Error(activeRes.data?.msg || '请求失败');
      }
      monthApiCache.value[cacheKey] = {
        新增: newRes.data?.data || [],
        活跃: activeRes.data?.data || []
      };
    }
    rebuildMonthSeries();
  } catch (e) {
    monthChartData.value = { years: months, series: [] };
    monthTableData.value = [];
    notification.error({ message: '月维度数据加载失败', description: e?.message || e });
  } finally {
    monthSpinning.value = false;
  }
};

// 页面加载时抓取月维度
onMounted(() => {
  fetchMonthSeries();
});

// 项目维度 / 年份变化 => 重新请求（使用缓存避免重复请求）
watch([project, year], () => fetchMonthSeries());

// 组织维度变化 => 仅前端重算
watch([employerCheckedList], () => rebuildMonthSeries());

</script>

<style scoped>
.container {
  margin: 20px 0;
  background-color: #fff;
  border: 1px solid #e5e7eb;
  border-radius: 16px;
  padding: 16px 24px;
}

.title-container {
  padding: 16px 0;
  text-align: left;
}

.title {
  font-size: 18px;
  font-weight: 600;
  color: #111111;
}

.avg-time-card {
  flex: 1;
  text-align: left;
  align-items: center;
  padding: 24px;
  background-color: #1E6CFF;
  border-radius: 12px;
  box-shadow: 0 8px 24px rgba(30, 108, 255, 0.18);
  transition: box-shadow 0.25s ease;
}

.avg-time-title {
  font-size: 14px;
  font-weight: 500;
  color: #FFFFFFEE;
}

.avg-time-number {
  font-size: 42px;
  font-weight: 700;
  color: #FFFFFF;
}

.avg-time-unit {
  font-size: 18px;
  font-weight: 500;
  color: #FFFFFFEE;
  margin-left: 4px;
}

.avg-time-description {
  flex: 1;
  min-width: 70%;
  text-align: left;
  border: 2px solid #E5E7EB;
  border-radius: 12px;
  font-size: 14px;
  color: #6B7280;
  line-height: 20px;
  margin-left: 24px;
  padding: 24px 24px 0 24px;
}

.contrib-box-row {
  padding: 0 24px;
  display: flex;
  gap: 16px;
}

.contrib-box {
  flex: 1;
  min-width: 140px;
  background-color: #F9FAFB;
  border: 2px solid;
  border-radius: 10px;
  padding: 12px;
  text-align: center;
}

.box-line1 {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 8px;
  font-weight: 600;
  color: #111827;
}

.box-dot {
  font-family: 'MS Mincho', serif;
  font-size: 16px;
  line-height: 1;
}

.box-label {
  font-size: 14px;
}

.box-line2 {
  margin-top: 8px;
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 6px;
}

.box-user-icon {
  color: #9CA3AF;
  font-size: 14px;
}

.box-number {
  font-size: 18px;
  font-weight: 700;
  color: #111827;
}

.box-line3 {
  margin-top: 4px;
  font-size: 12px;
  color: #6B7280;
}

.rise-icon {
  color: #00A63E;
  background-color: #DBFCE7;
  border-radius: 4px;
  width: 25px;
  height: 25px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  margin-right: 8px;
}

.caption-and-select-container {
  text-align: left;
  margin-right: 24px;
}

.caption {
  margin-right: 8px;
  font-weight: 500;
  font-size: 14px;
  color: #333333;
}

.select {
  width: 120px;
}

.chart-table-title {
  text-align: left;
  font-size: 16px;
  color: #111111;
}
</style>